import { useCallback, useEffect, useRef, useState } from 'react';
import {
  TranscriptLineData,
  VideoTranscriptData,
  advanceVideoTranscription,
  fetchVideoTranscript,
  startVideoTranscription,
} from '../../graphqlService';

/**
 * A video's transcript, kept moving while the page is open.
 *
 * The server transcribes five minutes per request, so the page asks for the
 * next five minutes until it's done, showing lines as they arrive. A
 * transcript that another page is already working on (or that the cron will
 * finish) is simply polled. Closing the page loses nothing.
 */

export type TranscriptPhase = 'loading' | 'none' | 'working' | 'done' | 'failed' | 'error';

export interface VideoTranscriptState {
  phase: TranscriptPhase;
  transcript: VideoTranscriptData | null;
  lines: TranscriptLineData[];
  /** A problem loading or starting, in words for the person. */
  problem: string | null;
  starting: boolean;
  start: (language?: string | null) => Promise<void>;
  retry: () => void;
}

interface Options {
  /** Start one automatically when the video has none (and starting is allowed). */
  autoStart?: boolean;
  canStart?: boolean;
  /** For tests: pause between polls when another page holds the work. */
  pollMs?: number;
  /** Load every word's time with the lines (to mark words in the transcript). */
  withWords?: boolean;
}

const MAX_ERRORS_IN_A_ROW = 4;

const messageOf = (error: unknown) => (error instanceof Error ? error.message : String(error ?? ''));

const describe = (error: unknown): string => {
  const message = messageOf(error);
  if (/allowance|isn’t stored|no sound|MP4 and MOV|format can’t|up to \d+ hours|couldn’t be read|not available/i.test(message)) {
    return message;
  }
  if (/access|permission|forbidden/i.test(message)) return 'You don’t have access to this video’s transcript.';
  if (/network|fetch|timed? ?out|offline|load failed/i.test(message)) return 'The transcript couldn’t be loaded. Check your connection, then try again.';
  return 'The transcript couldn’t be loaded. Try again in a moment.';
};

const phaseOf = (transcript: VideoTranscriptData | null): TranscriptPhase => {
  if (!transcript) return 'none';
  if (transcript.status === 'done') return 'done';
  if (transcript.status === 'failed') return 'failed';
  return 'working';
};

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function useVideoTranscript(
  videoId: string | null | undefined,
  { autoStart = false, canStart = false, pollMs = 4000, withWords = false }: Options = {}
): VideoTranscriptState {
  const [transcript, setTranscript] = useState<VideoTranscriptData | null>(null);
  const [lines, setLines] = useState<TranscriptLineData[]>([]);
  const [phase, setPhase] = useState<TranscriptPhase>('loading');
  const [problem, setProblem] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [nonce, setNonce] = useState(0);
  // Bumped on every new video or retry: an older loop sees it changed and stops.
  const generationRef = useRef(0);
  const autoStartedRef = useRef<string | null>(null);

  const loadLines = useCallback(async (id: string, generation: number) => {
    const full = await fetchVideoTranscript(id, withWords ? { withLines: true, withWords: true } : { withLines: true });
    if (generation !== generationRef.current) return null;
    setTranscript(full);
    setLines(full?.segments ?? []);
    return full;
  }, [withWords]);

  /** Asks for the next five minutes until done; refreshes the lines as chunks finish. */
  const drive = useCallback(async (id: string, generation: number, initial: VideoTranscriptData) => {
    let current = initial;
    let errors = 0;
    while (generation === generationRef.current && (current.status === 'queued' || current.status === 'running')) {
      try {
        const before = current.chunksDone;
        const advanced = await advanceVideoTranscription(id);
        if (generation !== generationRef.current) return;
        errors = 0;
        if (advanced) current = advanced;
        if (current.chunksDone !== before || current.status === 'done' || current.status === 'failed') {
          const full = await loadLines(id, generation);
          if (full) current = full;
        } else {
          setTranscript(current);
          // Someone else holds this stretch (another page, or the cron).
          await wait(pollMs);
        }
        if (generation !== generationRef.current) return;
        setPhase(phaseOf(current));
      } catch (error) {
        if (generation !== generationRef.current) return;
        errors += 1;
        if (errors >= MAX_ERRORS_IN_A_ROW) {
          setProblem(describe(error));
          setPhase('error');
          return;
        }
        await wait(Math.min(30000, pollMs * 2 ** errors));
      }
    }
  }, [loadLines, pollMs]);

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    setTranscript(null);
    setLines([]);
    setProblem(null);
    if (!videoId) {
      setPhase('none');
      return undefined;
    }
    setPhase('loading');
    (async () => {
      try {
        const found = await loadLines(videoId, generation);
        if (generation !== generationRef.current) return;
        if (!found && autoStart && canStart && autoStartedRef.current !== videoId) {
          autoStartedRef.current = videoId;
          setStarting(true);
          try {
            const started = await startVideoTranscription(videoId);
            if (generation !== generationRef.current) return;
            setTranscript(started);
            setPhase(phaseOf(started));
            await drive(videoId, generation, started);
          } finally {
            if (generation === generationRef.current) setStarting(false);
          }
          return;
        }
        setPhase(phaseOf(found));
        if (found && (found.status === 'queued' || found.status === 'running')) await drive(videoId, generation, found);
      } catch (error) {
        if (generation !== generationRef.current) return;
        setProblem(describe(error));
        setPhase('error');
      }
    })();
    return () => {
      generationRef.current += 1;
    };
  }, [videoId, autoStart, canStart, nonce, loadLines, drive]);

  const start = useCallback(async (language?: string | null) => {
    if (!videoId) return;
    generationRef.current += 1;
    const generation = generationRef.current;
    setStarting(true);
    setProblem(null);
    try {
      const started = await startVideoTranscription(videoId, language);
      if (generation !== generationRef.current) return;
      setTranscript(started);
      setPhase(phaseOf(started));
      setStarting(false);
      await drive(videoId, generation, started);
    } catch (error) {
      if (generation !== generationRef.current) return;
      setProblem(describe(error));
      setPhase((current) => (current === 'loading' ? 'none' : current));
    } finally {
      if (generation === generationRef.current) setStarting(false);
    }
  }, [drive, videoId]);

  const retry = useCallback(() => setNonce((value) => value + 1), []);

  return { phase, transcript, lines, problem, starting, start, retry };
}
